import React from 'react';
import { Clock as ClockIcon } from 'lucide-react';

export const NoteModal = ({ expenseId, noteText, setNoteText, onSubmit, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
      <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Add Note to {expenseId}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="close-note-modal">×</button>
      </div>
      <form onSubmit={onSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Note *</label>
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} required rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter your note here..." data-testid="note-textarea" />
        </div>
        <div className="flex justify-end space-x-4 pt-4">
          <button type="button" onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition">Cancel</button>
          <button type="submit" className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition shadow-lg" data-testid="submit-note-button">Add Note</button>
        </div>
      </form>
    </div>
  </div>
);

export const AuditModal = ({ expense, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
      <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Audit Log - {expense.expense_id}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="close-audit-modal">×</button>
      </div>
      <div className="p-6 overflow-y-auto flex-1">
        {expense.audit_logs && expense.audit_logs.length > 0 ? (
          <div className="space-y-4">
            {expense.audit_logs.map((log) => (
              <div key={`${log.timestamp}-${log.action}-${log.user_email}`} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="w-4 h-4 text-purple-600" />
                    <span className="font-semibold text-gray-800 capitalize">{log.action}</span>
                  </div>
                  <span className="text-xs text-gray-500">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
                <div className="ml-6">
                  <p className="text-sm text-gray-600"><span className="font-medium">User:</span> {log.user_name} ({log.user_email})</p>
                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-gray-700">Changes:</p>
                      <div className="mt-1 bg-gray-100 rounded p-2 text-xs font-mono">{JSON.stringify(log.changes, null, 2)}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (<p className="text-center text-gray-500 py-8">No audit logs available</p>)}
      </div>
      <div className="border-t border-gray-200 px-6 py-4">
        <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition">Close</button>
      </div>
    </div>
  </div>
);

export const CashAdvanceModal = ({ data, setData, onSubmit, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Log Cash Advance</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="close-cash-advance-modal">×</button>
      </div>
      <form onSubmit={onSubmit} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">User Email *</label>
          <input type="email" required value={data.user_email}
            onChange={(e) => setData({ ...data, user_email: e.target.value })}
            placeholder="user@roastery.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹) *</label>
          <input type="number" step="0.01" required value={data.amount}
            onChange={(e) => setData({ ...data, amount: parseFloat(e.target.value) })}
            placeholder="5000.00"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
          <input type="date" required value={data.date}
            onChange={(e) => setData({ ...data, date: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
          <textarea value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            rows="2" placeholder="Optional notes..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex justify-end space-x-4 pt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600" data-testid="submit-cash-advance-button">Log Cash Advance</button>
        </div>
      </form>
    </div>
  </div>
);
